import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Check, Plus, Tag } from 'lucide-react';
import { useState } from 'react';
import { LABEL_COLORS, type TagDto } from '@cw/shared';
import { api } from '@/lib/api';
import { handleFormError } from '@/lib/forms';
import { projectKeys, useTags } from '@/lib/projects';
import { useCan } from '@/lib/session';
import { cn } from '@/lib/utils';
import { Badge } from './badge';
import { Popover, PopoverContent, PopoverTrigger } from './popover';

export function TagChip({ tag }: { tag: TagDto }) {
  return (
    <Badge tone={tag.color} className="h-[22px] px-2 text-[11.5px]">
      {tag.name}
    </Badge>
  );
}

/** Seleção múltipla de tags com criação na hora (quem tem `tags:manage`). */
export function TagPicker({ value, onChange }: { value: string[]; onChange: (ids: string[]) => void }) {
  const tags = useTags();
  const qc = useQueryClient();
  const canCreate = useCan('tags:manage');
  const [q, setQ] = useState('');
  const create = useMutation({
    mutationFn: (name: string) =>
      api.post<TagDto>('/tags', {
        name,
        color: LABEL_COLORS[(tags.data?.length ?? 0) % LABEL_COLORS.length],
      }),
    onSuccess: (t) => {
      qc.invalidateQueries({ queryKey: projectKeys.tags });
      onChange([...value, t.id]);
      setQ('');
    },
    onError: (e) => handleFormError(e),
  });
  const all = tags.data ?? [];
  const selected = all.filter((t) => value.includes(t.id));
  const filtered = all.filter((t) => t.name.toLowerCase().includes(q.toLowerCase()));
  const exact = all.some((t) => t.name.toLowerCase() === q.trim().toLowerCase());
  const toggle = (id: string) =>
    onChange(value.includes(id) ? value.filter((v) => v !== id) : [...value, id]);

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="flex min-h-10 w-full flex-wrap items-center gap-1.5 rounded-control bg-canvas/60 px-2.5 py-1.5 text-left shadow-[inset_0_0_0_1px_var(--color-line-strong)] hover:shadow-[inset_0_0_0_1px_rgb(255_255_255/0.2)]"
        >
          {selected.length ? (
            selected.map((t) => <TagChip key={t.id} tag={t} />)
          ) : (
            <span className="flex items-center gap-2 text-[14px] text-fg-subtle">
              <Tag className="size-4" /> Adicionar tags
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-72 p-2">
        <input
          autoFocus
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && q.trim() && !exact && canCreate) {
              e.preventDefault();
              create.mutate(q.trim());
            }
          }}
          placeholder={canCreate ? 'Buscar ou criar tag' : 'Buscar tag'}
          className="mb-1 h-9 w-full rounded-[8px] bg-canvas/60 px-2.5 text-[13.5px] outline-none placeholder:text-fg-subtle"
        />
        <ul className="max-h-60 overflow-y-auto">
          {filtered.map((t) => (
            <li key={t.id}>
              <button
                type="button"
                onClick={() => toggle(t.id)}
                className="flex h-9 w-full items-center gap-2.5 rounded-[8px] px-2 text-left text-[13.5px] hover:bg-hover"
              >
                <span
                  className="size-2.5 rounded-full"
                  style={{ background: `var(--color-label-${t.color})` }}
                />
                <span className="flex-1 truncate">{t.name}</span>
                <Check className={cn('size-4 text-signal', !value.includes(t.id) && 'invisible')} />
              </button>
            </li>
          ))}
          {canCreate && q.trim() && !exact && (
            <li>
              <button
                type="button"
                onClick={() => create.mutate(q.trim())}
                className="flex h-9 w-full items-center gap-2.5 rounded-[8px] px-2 text-left text-[13.5px] text-fg-muted hover:bg-hover hover:text-fg"
              >
                <Plus className="size-4" /> Criar “{q.trim()}”
              </button>
            </li>
          )}
          {!filtered.length && !q && (
            <li className="px-2 py-3 text-[13px] text-fg-subtle">Nenhuma tag ainda.</li>
          )}
        </ul>
      </PopoverContent>
    </Popover>
  );
}
